import { useState } from "react";
import type { ReactNode } from "react";
import { useCopy } from "../../../shared/i18n";
import { MfaEnrolmentBody, RecoveryCodesPanel } from "./MfaEnrolment";
import { ChangePasswordStep } from "./ChangePasswordStep";
import type { StaffSignIn } from "../signInFlow";
import { PasswordInput } from "../../../shared/components";
import { AuthHeading } from "../../console/public/AuthSplit";
import { OperatorAuthShell } from "../../console/public/OperatorAuthShell";
import { FormAlert, TextInput } from "../../console/public/fields";
import { IconLock, IconMail } from "../../console/public/icons";
import {
  FIELD_BASE,
  FIELD_WITH_ICON,
  LABEL,
  LINK_QUIET,
  SUBMIT_FULL,
} from "../../console/public/styles";
import type {
  Door,
  DoorOutcome,
  EnrolmentOutcome,
} from "../../../app/router/routes";

/**
 * A6 — the platform operator's own front door.
 *
 * 🔴 WHY THERE ARE TWO SIGN-IN SCREENS IN A PRODUCT WHOSE A2 IS *"ONE SCREEN FOR
 * EVERY STAFF ROLE"*.
 *
 * A2's rule is about the FORM, and it is untouched here: there is no role picker
 * on this page, it POSTs to the same `POST /auth/login`, and it asserts nothing
 * the server would then have to disprove. Role comes from the account.
 *
 * What differs is the audience, and it differs completely. A2 is reached from
 * A1 — a page whose largest element is a play tile for a four-year-old — and
 * every sentence on it is written for a school: *"Teachers, school admins and
 * operators all sign in here"*, *"your school admin sends an invitation"*.
 * Neither is true of the person this screen is for. There are a handful of
 * operator accounts in existence, the first was created by `prisma/seed.ts` at
 * deploy because *nothing in the product can create it*, and no invitation flow
 * reaches them. Sending that person through a child's picker is not a security
 * problem; it is a screen that lies to them daily.
 *
 * ⚠️ AND IT IS A DIFFERENT HOSTNAME (`shared/lib/host.ts`), WHICH IS THE POINT
 * OF THE SEPARATION AND ALSO ITS LIMIT. `operator.kalqy.app` serves the same
 * bundle as the school app; the split means a school never learns this door
 * exists, not that the door is stronger. What actually stops a school admin
 * reaching the platform tier is `@Roles('SUPER_ADMIN')` in the API, and it stops
 * them identically whichever hostname they typed. Stated here because a screen
 * this deliberately separate invites the belief that the separation is the
 * control.
 *
 * 🔴 THE REFUSAL IS STILL GENERIC, EXACTLY AS A2's IS. Unknown email, wrong
 * password, deactivated account: one sentence. `client.ts` discards response
 * bodies, so the only input is a status, and there is nothing here that could
 * tell them apart even if it wanted to.
 */

/* ─────────────────────────────────────────────────────────── the form ── */

/**
 * ⚠️ THE CONSOLE LOOK (Figma P3 "Sign in", 11:439, with "Kalqy team" chosen).
 * Operators spend their day in the console; the door into it now wears the
 * same Inter, ink-on-parchment field and black button as every screen behind
 * it, so a support call about a screen is about the design system both sides
 * are looking at.
 */
const FIELD = FIELD_BASE;
const SUBMIT = `${SUBMIT_FULL} mt-6`;

/** Icon + colour + text, announced — never colour alone. */
function Alert({
  children,
  testId,
  tone = "danger",
}: {
  children: ReactNode;
  testId?: string;
  tone?: "danger" | "warning";
}) {
  return (
    <div className="mt-4">
      <FormAlert tone={tone} testId={testId}>
        {children}
      </FormAlert>
    </div>
  );
}

export interface OperatorLoginScreenProps {
  /**
   * Given both credentials. Resolves with what the account turned out to be —
   * the screen never guesses, and never asks for a role.
   *
   * 🔴 IT RETURNS `'not-operator'` RATHER THAN THROWING, BECAUSE IT IS NOT A
   * FAILURE. The credentials were correct. What happened is that a school
   * account arrived at the platform door, and the honest thing is to say so and
   * point at the right one — not to render "that email and password did not
   * match" at somebody whose email and password matched perfectly.
   *
   * ⚠️ IT LEAKS NOTHING. Distinguishing here requires having already
   * authenticated as that very account, so the only person who can read this
   * answer is the person who already holds the credential it describes. The
   * enumeration oracle A2 refuses to build is one that answers BEFORE a
   * successful sign-in, and this is strictly after.
   */
  onSignIn?: (email: string, password: string) => Promise<StaffSignIn>;
  /**
   * P3-16 — the forced change, should an operator's password have been chosen
   * by somebody else. Operators are invited and choose their own, so this is
   * rarely reached; it is here because the server, not this screen, decides it,
   * and a step this door could not render would leave the button spinning.
   */
  onChangePassword?: (newPassword: string) => Promise<StaffSignIn>;
  /**
   * The compulsory first enrolment.
   *
   * 🔴 IT HAPPENS HERE, ON THE SIGN-IN SCREEN, AND THAT IS THE POINT. A second
   * factor is compulsory for operators, so refusing an un-enrolled one and
   * telling them to "set it up in Settings" would be a locked door with no
   * handle — Settings is behind the sign-in that just refused them.
   */
  onStartEnrolment?: () => Promise<{
    secret: string;
    qrSvg: string;
    otpauthUri: string;
  }>;
  /** Resolves with the recovery codes — the only copy that will ever exist. */
  onFinishEnrolment?: (code: string) => Promise<EnrolmentOutcome>;
  /** Called once the operator confirms they have saved the codes. */
  onEnterAfterEnrolment?: () => void;
  /**
   * The second step, when the account has an authenticator.
   *
   * 🔴 THE SCREEN HOLDS NEITHER THE PASSWORD NOR THE CHALLENGE TOKEN. It has a
   * text box and this callback; the router keeps the five-minute assertion. The
   * shape that keeps `email`/`password` in component state so it can re-submit
   * them with the code leaves a plaintext credential in a React state variable —
   * and in every DevTools inspection and heap dump — for as long as it takes
   * somebody to unlock a phone and read six digits.
   */
  onSubmitCode?: (code: string) => Promise<DoorOutcome>;
  /** Back to the password form. Discards the assertion rather than keeping it warm. */
  onCancelCode?: () => void;
  /**
   * Where the account that just signed in actually belongs. Rendered as the one
   * way off this page.
   *
   * ⚠️ IT TAKES THE DOOR RATHER THAN ASSUMING THE TEACHER ONE. There are two
   * school doors now, and sending a school admin to the teacher form would be a
   * second wrong address handed out immediately after the first.
   */
  onDoor?: (door: Door) => void;
}

export function OperatorLoginScreen({
  onSignIn,
  onChangePassword,
  onSubmitCode,
  onCancelCode,
  onStartEnrolment,
  onFinishEnrolment,
  onEnterAfterEnrolment,
  onDoor,
}: OperatorLoginScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  /**
   * 🔴 THE CODE STEP IS A STATE OF THIS SCREEN, NOT A ROUTE.
   *
   * A `#/operator/mfa` route would be a URL somebody can reload, bookmark, or
   * arrive at with no challenge open — three states to handle for a step that
   * lasts five minutes and cannot survive a refresh anyway (the assertion lives
   * in the router's memory, and `staffSession.ts` deliberately keeps nothing in
   * `localStorage`). `kidFlow.ts` makes the same call one tier down: a flow is a
   * state machine, not a URL.
   */
  const [step, setStep] = useState<
    "password" | "code" | "enrol" | "recovery" | "change-password"
  >("password");
  const [code, setCode] = useState("");
  /** The QR and setup key, held only for as long as the enrolment step is open. */
  const [enrolment, setEnrolment] = useState<{
    secret: string;
    qrSvg: string;
    otpauthUri: string;
  } | null>(null);
  /** 🔴 Shown once. Only their hashes are stored server-side. */
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  /**
   * Which door the account that just signed in actually belongs at.
   *
   * ⚠️ SET ONLY ALONGSIDE `state === 'not-operator'`, so it can never point
   * somewhere while the form is showing an ordinary refusal.
   */
  const [_rightDoor, setRightDoor] = useState<Door | null>(null);
  const [state, setState] = useState<
    | "idle"
    | "signing-in"
    | "refused"
    | "not-operator"
    | "throttled"
    | "failed"
    /**
     * 🔴 A 5xx IS NOT A CONNECTION PROBLEM, AND CONFLATING THE TWO COST AN
     * EVENING. `/auth/login/mfa` answers 503 when `TOTP_ENC_KEY` cannot decrypt
     * the stored secret — the request arrived, the server answered, and the
     * account is unusable until somebody fixes the deployment. Reported as
     * `failed` it read *"check the connection"*, so an operator debugged their
     * wifi while the server had already said exactly what was wrong.
     */
    | "server-fault"
    /** The password was right and the code was not — a different fact, different sentence. */
    | "code-refused"
  >("idle");

  const t = useCopy({
    en: {
      tier: "Platform operator",
      title: "Operator sign-in",
      email: "Email",
      password: "Password",
      submit: "Sign in",
      signingIn: "Signing in…",
      /* 🔴 One sentence for every wrong answer, exactly as A2 does it. */
      refused: "That email and password did not match.",
      /* Correct credentials, wrong door. A different fact, so a different line. */
      notOperator:
        "That account is not a platform operator. It signs in on the school app, and this page cannot open it.",
      throttled: "Too many attempts from here. Wait a minute and try again.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      /* ⚠️ IT DOES NOT ASK FOR A RETRY, because a retry cannot fix it. A 5xx
         here is a deployment fault — a TOTP key that does not match the stored
         secret, a database the API cannot reach — and this tier's own rule is
         that a lost operator factor is restored by another operator. */
      serverFault:
        "Something is wrong at Kalqy’s end, and signing in again will not fix it. Ask another operator to check the server.",
      /* ── the second factor ── */
      codeTitle: "Authenticator code",
      /* 🔴 The 30 seconds is named, because it is the thing that makes a code
         "wrong" a second after it was right. */
      codeSubtitle:
        "Open your authenticator app and type the 6-digit code it is showing. It changes every 30 seconds.",
      codeLabel: "Code",
      /* ⚠️ One box for both, because somebody reaching for a recovery code is by
         definition unable to produce an authenticator one — asking them to first
         classify their own problem is the moment they are least able to. */
      codeHint:
        "A recovery code works here too, if you cannot reach your phone.",
      codeSubmit: "Sign in",
      codeChecking: "Checking…",
      codeBack: "Start again",
      /* Both causes in one sentence: the response body is discarded, so the
         screen genuinely cannot tell an expired challenge from a wrong code. */
      codeRefused:
        "That code did not match, or the sign-in took too long. Try the current code, or start again.",
      /* ── the compulsory first enrolment ── */
      enrolTitle: "Set up your authenticator",
      /**
       * 🔴 IT SAYS WHY THIS ACCOUNT, NOT "SECURITY IS IMPORTANT". An operator
       * account has no school of its own and can open every school on Kalqy —
       * that fact is what makes thirty seconds of setup obviously worth it, and
       * a generic sentence about protection is not.
       */
      enrolWhy:
        "Operator accounts can open every school on Kalqy, so a password on its own is not enough. This takes about a minute and you only do it once.",
      enrolStep1:
        "Install an authenticator app if you do not have one — Google Authenticator, Authy and 1Password all work.",
      enrolStep2: "Scan this code with it.",
      enrolManual: "Cannot scan? Enter this setup key by hand instead.",
      enrolSecretLabel: "Setup key",
      enrolCodeLabel: "Now type the 6-digit code the app shows",
      enrolSubmit: "Turn it on and sign in",
      enrolPreparing: "Preparing…",
      enrolFailed: "Could not start the setup. Go back and sign in again.",
      /* ── the recovery codes, shown once ── */
      recoveryTitle: "Save your recovery codes",
      /* The same three-sentence shape the school-admin credential panel uses,
         because the constraint is identical: this is the only copy. */
      recoveryBody:
        "Each of these signs you in once if you lose your phone. Save them somewhere that is not your phone — this is the only time they are shown.",
      recoveryWarn:
        "Without them, a lost phone means another operator has to rebuild your account.",
      recoveryDone: "I have saved them — continue",
      /* ── the console page (Figma P3) ── */
      consoleSubtitle: "Choose who you are, then use your work email.",
      workEmail: "Work email",
      emailPlaceholder: "you@kalqy.in",
      forgot: "Forgot password?",
      forgotNote:
        "Ask another Kalqy operator to issue you a new invitation — there is no self-service reset for operator accounts.",
    },
  });

  const busy = state === "signing-in";
  const [forgotOpen, setForgotOpen] = useState(false);

  async function submit() {
    if (!onSignIn) return;

    setState("signing-in");
    try {
      await follow(await onSignIn(email.trim(), password));
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      /*
       * ⚠️ 0 IS "NO ANSWER AT ALL" — offline, DNS, timeout (`client.ts`) — and it
       * is separated from a refusal because somebody on a dropped connection who
       * reads "wrong password" will start changing a password that was never
       * wrong. 429 is the rate limit, which says nothing about any account.
       */
      if (status === 429) setState("throttled");
      /*
       * ⚠️ THERE IS NO LONGER A "YOU MUST ENROL" REFUSAL TO HANDLE, and its
       * absence is the whole point of this round of work. An operator with no
       * second factor used to get a 403 telling them to enrol on a machine they
       * had just been refused entry to; now `login` answers with an enrolment
       * challenge and the screen opens the setup step. 401 stays generic.
       */
      else if (status === 401 || status === 403) setState("refused");
      else if (typeof status === "number" && status >= 500)
        setState("server-fault");
      else setState("failed");
    }
  }

  /**
   * What happens once the server has accepted the password — on the form and
   * again after a forced change, which continues the same sign-in.
   */
  async function follow(outcome: StaffSignIn): Promise<void> {
    /*
     * On `'operator'` there is deliberately no success state: the router
     * replaces this screen the moment the session exists, and a "signed in!"
     * flash would be a frame of chrome between a password and a dashboard.
     */
    if (outcome.at === "change-password") {
      setPassword("");
      setCode("");
      setState("idle");
      setStep("change-password");
    } else if (outcome.at === "wrong-door") {
      setRightDoor(outcome.door);
      setPassword("");
      setState("not-operator");
    } else if (outcome.at === "enrol") {
      /*
       * 🔴 COMPULSORY, AND THIS IS THE ONLY ROUTE TO A FIRST FACTOR. The
       * password is dropped here as it is on the code path — the router holds
       * a fifteen-minute token instead, so nothing on this screen is a
       * credential while somebody installs an app.
       */
      setPassword("");
      setCode("");
      setState("idle");
      setStep("enrol");
      try {
        setEnrolment((await onStartEnrolment?.()) ?? null);
      } catch {
        setState("failed");
        setStep("password");
      }
    } else if (outcome.at === "mfa") {
      /*
       * 🔴 THE PASSWORD IS DROPPED HERE, NOT KEPT FOR A RE-SUBMIT. The router
       * holds a five-minute assertion instead, so nothing on this screen is a
       * credential once the code step opens.
       */
      setPassword("");
      setCode("");
      setStep("code");
      setState("idle");
    }
  }

  async function submitCode() {
    if (!onSubmitCode) return;

    setState("signing-in");
    try {
      const outcome = await onSubmitCode(code.trim());
      /* Same as above: no success state on the way in. A school account that got
         this far is still told plainly and pointed at the right door. */
      if (outcome.at === "wrong-door") {
        setRightDoor(outcome.door);
        setStep("password");
        setState("not-operator");
      }
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      /*
       * ⚠️ AN EXPIRED CHALLENGE AND A WRONG CODE ARE THE SAME 401 HERE, and the
       * screen must not guess between them: `client.ts` discards response
       * bodies, so it genuinely cannot tell. `codeRefused` names both, because
       * the two have different fixes and only the person knows which happened.
       */
      else if (status === 401 || status === 403) setState("code-refused");
      else if (typeof status === "number" && status >= 500)
        setState("server-fault");
      else setState("failed");
    }
  }

  function backToPassword() {
    onCancelCode?.();
    setStep("password");
    setCode("");
    setEnrolment(null);
    setState("idle");
  }

  /**
   * Finish the compulsory enrolment.
   *
   * 🔴 IT DOES NOT NAVIGATE. The response carries the recovery codes — the only
   * copy that will ever exist — so the screen moves to the `recovery` step and
   * waits for the operator to say they have saved them. Replacing the screen
   * here would destroy them on the way past.
   */
  async function submitEnrolment() {
    if (!onFinishEnrolment) return;

    setState("signing-in");
    try {
      const outcome = await onFinishEnrolment(code.trim());
      /*
       * ⚠️ REACHABLE HERE TOO, THOUGH ONLY JUST. Enrolment is compulsory for
       * operators, so a challenge at THIS door is almost always an operator —
       * but "almost always" is decided by a server rule (`isRequiredFor`) that
       * this screen does not get to assume, and assuming it is how a second
       * MFA-required role would silently be let through the wrong door.
       */
      if (outcome.at === "wrong-door") {
        setRightDoor(outcome.door);
        setCode("");
        setEnrolment(null);
        setStep("password");
        setState("not-operator");
        return;
      }
      setRecoveryCodes(outcome.codes);
      setCode("");
      setEnrolment(null);
      setStep("recovery");
      setState("idle");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      else if (status === 401 || status === 403) setState("code-refused");
      else if (typeof status === "number" && status >= 500)
        setState("server-fault");
      else setState("failed");
    }
  }

  return (
    <OperatorAuthShell testId="operator-signin-console">
      {/*
        🔴 THE CODE STEP REPLACES THE FORM RATHER THAN APPEARING UNDER IT. A
        password field still on screen beside a code field invites somebody to
        retype the password when the code is refused — which cannot help, and
        which trains people to treat a second factor as a second password.
      */}
      {step === "recovery" ? (
        <RecoveryCodesPanel
          codes={recoveryCodes}
          submitClass={SUBMIT}
          onDone={() => onEnterAfterEnrolment?.()}
        />
      ) : step === "change-password" ? (
        <ChangePasswordStep
          idPrefix="operator"
          submitClass={SUBMIT}
          renderAlert={(children, testId) => (
            <Alert testId={testId}>{children}</Alert>
          )}
          onSubmit={async (next) => {
            if (!onChangePassword) return;
            await follow(await onChangePassword(next));
          }}
          onBack={backToPassword}
        />
      ) : step === "enrol" ? (
        <MfaEnrolmentBody
          idPrefix="operator"
          enrolment={enrolment}
          code={code}
          onCode={setCode}
          busy={busy}
          problem={
            state === "code-refused"
              ? "code"
              : state === "throttled"
                ? "throttled"
                : state === "failed" || state === "server-fault"
                  ? "failed"
                  : null
          }
          onSubmit={() => void submitEnrolment()}
          onBack={backToPassword}
          fieldClass={FIELD}
          submitClass={SUBMIT}
          renderAlert={(children, testId) => (
            <Alert testId={testId}>{children}</Alert>
          )}
        />
      ) : step === "code" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitCode();
          }}
        >
          <AuthHeading title={t.codeTitle} subtitle={t.codeSubtitle} />

          <label className={`${LABEL} mt-6`} htmlFor="operator-mfa-code">
            {t.codeLabel}
          </label>
          <input
            id="operator-mfa-code"
            /*
             * 🔴 `text`, NOT `number`. A recovery code has letters and a dash,
             * and a number input silently strips a leading zero. `inputMode`
             * still opens the digit keypad on a phone.
             */
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={64}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            className={`${FIELD} mt-2 tracking-[0.3em]`}
          />
          <p className="mt-2 text-[12px] leading-4 text-muted">{t.codeHint}</p>

          <button
            type="submit"
            disabled={busy || code.trim().length === 0}
            className={SUBMIT}
          >
            {busy ? t.codeChecking : t.codeSubmit}
          </button>

          {state === "code-refused" && (
            <Alert testId="operator-code-refused">{t.codeRefused}</Alert>
          )}
          {state === "throttled" && <Alert>{t.throttled}</Alert>}
          {state === "server-fault" && (
            <Alert testId="operator-server-fault">{t.serverFault}</Alert>
          )}
          {state === "failed" && <Alert>{t.failed}</Alert>}

          <p className="mt-5 text-center">
            <button
              type="button"
              onClick={backToPassword}
              className={LINK_QUIET}
            >
              {t.codeBack}
            </button>
          </p>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {/*
            🔴 NO DOOR TABS. Kalqy's login is its own page (the product
            owner's rule), so nothing here offers or switches to the school or
            teacher sign-in — only the wrong-door message below points there.
          */}
          <AuthHeading title={t.title} />

          <label className={`${LABEL} mt-6`} htmlFor="operator-email">
            {t.workEmail}
          </label>
          <div className="mt-2">
            <TextInput
              id="operator-email"
              type="email"
              icon={IconMail}
              autoComplete="username"
              placeholder={t.emailPlaceholder}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </div>

          <label className={`${LABEL} mt-5`} htmlFor="operator-password">
            {t.password}
          </label>
          <div className="relative mt-2">
            <IconLock
              size={16}
              className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-ink-soft"
            />
            <PasswordInput
              id="operator-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              className={FIELD_WITH_ICON}
            />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              aria-expanded={forgotOpen}
              aria-controls="operator-forgot"
              onClick={() => {
                setForgotOpen((v) => !v);
              }}
              className="rounded-sm text-[13px] font-medium text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            >
              {t.forgot}
            </button>
          </div>
          {forgotOpen ? (
            <p
              id="operator-forgot"
              className="mt-2 rounded-md border border-line bg-surface px-3 py-2.5 text-[12.5px] leading-[18px] text-ink-soft"
            >
              {t.forgotNote}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={
              busy || email.trim().length === 0 || password.length === 0
            }
            className={SUBMIT}
          >
            {busy ? t.signingIn : t.submit}
          </button>

          {state === "refused" && (
            <Alert testId="operator-refused">{t.refused}</Alert>
          )}
          {state === "not-operator" && (
            <Alert testId="operator-wrong-door" tone="warning">
              {t.notOperator}
              {onDoor ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => {
                      onDoor("school");
                    }}
                    className="font-medium underline underline-offset-2"
                  >
                    Go to the school sign-in
                  </button>
                </>
              ) : null}
            </Alert>
          )}
          {state === "throttled" && <Alert>{t.throttled}</Alert>}
          {state === "server-fault" && (
            <Alert testId="operator-server-fault">{t.serverFault}</Alert>
          )}
          {state === "failed" && <Alert>{t.failed}</Alert>}
        </form>
      )}
    </OperatorAuthShell>
  );
}
